/* eslint-disable consistent-return */
/**
 *
 * LinkDetailCard
 *
 */

import React, { useEffect, useState } from 'react';
import _get from 'lodash/get';
import { Dialog, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import LinkDetailPopup from 'components/LinkItemSmall/LinkDetailPopup';
import { useSelector } from 'react-redux';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { getParentPosition } from 'utils/embedded';
import { VisitSiteButton } from './VisitSiteButton';

const useStyles = makeStyles(theme => ({
  contentWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: 30,
    borderRadius: 8,
    zIndex: 10,
    backgroundColor: '#fff',
    overflowY: props => props.inModal && 'auto',
  },
  paper: {
    maxWidth: '900px',
    width: '100%',
    borderRadius: '8px !important',
  },
  button: ({ componentOverrides }) => ({
    width: 'fit-content',
    padding: '12px 32px',
    marginTop: theme.spacing(2),
    ..._get(componentOverrides, 'VisitSiteButton', {}),
  }),
  closeButton: ({ componentOverrides }) => ({
    padding: 6,
    margin: '0px 0px 8px auto',
    ..._get(componentOverrides, 'closeButton', {}),
  }),
  contentContainer: ({ componentOverrides }) => ({
    display: 'flex',
    gap: 32,
    overflowY: props => props.inModal && 'auto',

    '& > div:first-child': {
      flexBasis: 'calc(60% - 8px)',
      flexGrow: 1,
      minWidth: 'min(100%, 400px)',
    },
    '& > div:last-child': {
      flexBasis: 'calc(40% - 8px)',
      flexGrow: 1,
    },
    [theme.breakpoints.down('960')]: {
      flexWrap: 'wrap',
    },
    ..._get(componentOverrides, 'Link', {}),
  }),
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
    marginBottom: theme.spacing(3),
  },
}));

function LinkDetailCard({
  item,
  onClose,
  onClick,
  previewMode,
  shouldDisplayVisitSiteButton,
  inModal = true,
  componentOverrides,
}) {
  const clientExclusiveCardStyles = _get(
    componentOverrides,
    'styles.AssessmentResult.ClientRelatedResources.ClientExclusiveCard',
    {},
  );
  const [embedParentPosition, setEmbedParentPosition] = useState({});
  const classes = useStyles({ inModal, componentOverrides });
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const clientDetails = useSelector(makeSelectClientDetails());
  const customNextTitle = clientDetails?.metadata?.nextStepsTitle;
  const cardTitle = item.title || item.name;
  const nextTitle =
    (previewMode ? _get(item, 'nextStepsTitle') : customNextTitle) ||
    _get(siteCopy, 'pageCopy.hybridLinkNextSteps');
  useEffect(() => {
    let isMounted = true;

    const fetchAndSetParentPosition = async () => {
      const parentPosition = await getParentPosition();
      if (isMounted) {
        setEmbedParentPosition(parentPosition);
      }
    };
    if (inModal) {
      fetchAndSetParentPosition();
    }

    return () => {
      isMounted = false;
    };
  }, [inModal]);
  const fontSize = {
    mobile: '1rem',
    tablet: '1rem',
    desktop: '1rem',
  };

  const content = (
    <div className={classes.contentWrapper}>
      <div className={classes.headerContainer}>
        <Typography id="title" variant="h4" color="textPrimary">
          {cardTitle}
        </Typography>
        <IconButton
          className={classes.closeButton}
          aria-label="Close"
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.contentContainer} tabIndex={-1}>
        {item.readMoreInfo && (
          <div>
            <RichTextReactRenderer
              data={item.readMoreInfo}
              fontSize={fontSize}
            />
          </div>
        )}
        {item.nextSteps && (
          <div>
            <Typography variant="h5" color="primary">
              {nextTitle}
            </Typography>
            <RichTextReactRenderer data={item.nextSteps} fontSize={fontSize} />
          </div>
        )}
      </div>
      {shouldDisplayVisitSiteButton && (
        <div>
          <VisitSiteButton
            title={cardTitle}
            clientDetails={clientDetails}
            className={classes.button}
            siteCopy={siteCopy}
            onClick={onClick}
            componentOverrides={clientExclusiveCardStyles}
          />
        </div>
      )}
    </div>
  );

  const embedPositionTop = _get(embedParentPosition, 'top', null);

  const dialogProps = {
    onClose,
    'aria-labelledby': 'title',
    open: true,
    classes: {
      paper: classes.paper,
    },
  };

  if (embedPositionTop && inModal) {
    dialogProps.PaperProps = {
      style: {
        position: 'fixed',
        top: `${embedPositionTop}px`,
      },
    };
  }

  if (inModal) {
    return <Dialog {...dialogProps}>{content}</Dialog>;
  }

  return <LinkDetailPopup onClose={onClose}>{content}</LinkDetailPopup>;
}

export default LinkDetailCard;
